// utils/darkModeSafety.js
// Safe darkMode initialization for the entire app

import React from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Global cache
let darkModeCache = null;
let isInitialized = false;

/**
 * Initialize darkMode once at app start
 * This ensures darkMode always exists before any component tries to use it
 */
export const initializeDarkMode = async () => {
  if (isInitialized) return darkModeCache;
  
  try {
    const stored = await AsyncStorage.getItem('userPreferences');
    
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        darkModeCache = parsed.darkMode === true;
      } catch (e) {
        darkModeCache = false;
      }
    } else {
      const defaultPreferences = {
        darkMode: false,
        language: 'English',
        region: 'United Kingdom',
        currency: 'GBP (£)',
        timeFormat: '24-hour',
        pushNotifications: true,
        emailNotifications: true,
        autoSave: true,
        emailConfirmation: true,
        autoBackup: true,
      };
      await AsyncStorage.setItem('userPreferences', JSON.stringify(defaultPreferences));
      darkModeCache = false;
    }
    
    isInitialized = true;
    console.log('✅ DarkMode initialized:', darkModeCache);
    return darkModeCache;
  } catch (error) {
    console.warn('⚠️ Failed to initialize darkMode, using default');
    darkModeCache = false;
    isInitialized = true;
    return darkModeCache;
  }
};

/**
 * Get darkMode value (synchronous, uses cache)
 * Safe to use anywhere, even before initialization
 */
export const getDarkMode = () => {
  return darkModeCache !== null ? darkModeCache : false;
};

/**
 * Set darkMode value
 */
export const setDarkMode = async (value) => {
  try {
    darkModeCache = Boolean(value);
    const stored = await AsyncStorage.getItem('userPreferences');
    let preferences = stored ? JSON.parse(stored) : {};
    preferences.darkMode = darkModeCache;
    await AsyncStorage.setItem('userPreferences', JSON.stringify(preferences));
    return true;
  } catch (error) {
    console.error('Error setting darkMode:', error);
    return false;
  }
};

/**
 * React Hook for components — must only be called inside a function component
 */
export const useDarkMode = () => {
  const [darkMode, setDarkModeState] = React.useState(getDarkMode());
  const [isLoading, setIsLoading] = React.useState(!isInitialized);

  React.useEffect(() => {
    if (!isInitialized) {
      initializeDarkMode().then(value => {
        setDarkModeState(value);
        setIsLoading(false);
      });
    }
  }, []);

  const updateDarkMode = async (value) => {
    const success = await setDarkMode(value);
    if (success) {
      setDarkModeState(value);
    }
    return success;
  };

  const toggleDarkMode = () => {
    updateDarkMode(!darkMode);
  };

  return {
    darkMode,
    isLoading,
    setDarkMode: updateDarkMode,
    toggleDarkMode,
  };
};

// Initialize immediately at module load (non-blocking, no hooks)
initializeDarkMode().catch(() => {});